import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { ApplicantListTab, getApplicantList } from "@endpoints/job.service";
import { ArrowRight, ChevronDown } from "lucide-react";
import React, {
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { JobDetailContext } from "../JobDetailPageLayout";
import { Link, useParams } from "react-router-dom";
import dayjs from "../../../lib/dayjs";
import LoadingAnimation from "@assets/svg/LoadingAnimation";

const statuses = [
  {
    label: "New",
    value: "new",
  },
  {
    label: "Reviewed",
    value: "reviewed",
  },
  {
    label: "Submitted",
    value: "submitted",
  },
  {
    label: "Rejected",
    value: "rejected",
  },
];

const orderings = [
  {
    label: "Highest match",
    value: "high",
  },
  {
    label: "Latest application",
    value: "latest",
  },
  {
    label: "Candidate name",
    value: "candidate",
  },
];

function getSearchPlaceholder(tab: ApplicantListTab) {
  switch (tab) {
    case "all":
      return "all applicants";
    case "priority":
      return "priority applicants";
    case "in-progress":
      return "applications in progress";
    case "below-threshold":
      return "filtered candidates";
    case "declined":
      "declined applicants";
      return;
    default:
      return "all applicants";
  }
}

function getEmptyState(tab: ApplicantListTab, searchText: string) {
  if (searchText) {
    return {
      title: "No applicants match this search",
      desc: "Adjust the search text or clear it to see the full list again.",
    };
  }
  switch (tab) {
    case "priority":
      return {
        title: "No priority applicants yet",
        desc: "Candidates that clear the current review threshold will appear here as they apply.",
      };
    case "in-progress":
      return {
        title: "No applications in progress",
        desc: "Applications move here once you start reviewing or submitting a candidate.",
      };
    case "below-threshold":
      return {
        title: "No candidates below the threshold",
        desc: "Profiles that fall short of the current evidence bar will be listed here.",
      };
    case "declined":
      return {
        title: "No declined applicants",
        desc: "Candidates you decline stay listed here and remain retrievable.",
      };
    default:
      return {
        title: "No applicants yet",
        desc: "New applications will appear here as candidates apply to this job post.",
      };
  }
}

interface GeneralApplicationTableProps {
  activeTab: ApplicantListTab;
}

const GeneralApplicationTable = ({
  activeTab,
}: GeneralApplicationTableProps) => {
  const { jobId } = useParams();
  const { applicantList, setApplicantList, setApplicantListStatistics } =
    useContext(JobDetailContext);

  // Local state
  const [filter, setFilter] = useState({
    status: "new",
    order: "high",
  });

  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [hasMoreData, setHasMoreData] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMoreData, setIsLoadingMoreData] = useState(false);

  const fetchApplicants = useCallback(async () => {
    if (!page || !activeTab || !jobId) return;
    try {
      if (page > 1) {
        setIsLoadingMoreData(true);
      } else {
        setIsLoading(true);
      }

      const response = await getApplicantList({
        page,
        jobId,
        searchText,
        tab: activeTab,
      });
      setHasMoreData(response?.pagination?.hasMore);
      setApplicantList(response?.data);
      setApplicantListStatistics({
        ...response?.statistics,
        reviewThreshold: response?.reviewThreshold,
        preferredQueueSize: response?.preferredQueueSize,
      });
    } catch (error) {
      console.error("Error while fetching applicants", error);
    } finally {
      setIsLoading(false);
      setIsLoadingMoreData(false);
    }
  }, [page, activeTab, jobId, searchText]);

  const handleLoadMore = useCallback(() => {
    if (isLoading || isLoadingMoreData || !hasMoreData) return;
    setPage((prev) => prev + 1);
  }, [page, isLoading, isLoadingMoreData, hasMoreData]);

  useEffect(() => {
    fetchApplicants();
  }, [fetchApplicants]);

  const emptyState = getEmptyState(activeTab, searchText);

  return (
    <div className="flex flex-col min-h-0 gap-3 mt-5 flex-1">
      {/* Review threshold info */}
      {activeTab === "below-threshold" ? (
        <div className="flex items-center justify-between border border-l-[4px] border-l-slate-600 rounded-md px-5 py-4">
          <div className="flex flex-col">
            <span className="font-bold text-light-black text-xss tracking-widest uppercase">
              Below the current review threshold
            </span>
            <span className="font-semibold text-md text-black mt-1">
              Below-threshold applicants remain available
            </span>
            <span className="font-medium text-xss text-light-black">
              These profiles have not been rejected. Review the evidence gap
              before taking any individual or bulk action.
            </span>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-slate-600 font-bold leading-3">41</span>
            <span className="text-slate-600 text-xss font-semibold">
              candidates
            </span>
          </div>
        </div>
      ) : null}

      {/* Filter section */}
      <div className="flex items-center gap-4">
        <input
          className="rounded-md border outline-none placeholder:text-light-black text-xs font-medium h-9 px-3 flex-1"
          placeholder={`Search ${getSearchPlaceholder(activeTab)}`}
          value={searchText}
          onChange={(e) => setSearchText(e?.target?.value)}
        />
        {/* Status Filter */}
        <DropdownMenu>
          <DropdownMenuTrigger
            defaultValue={statuses[0]?.value}
            className="flex items-center justify-between text-left text-charcoal font-medium text-xs  border rounded-md h-max py-1.5 px-2 w-40"
          >
            {statuses?.find((s) => filter?.status === s?.value)?.label}
            <ChevronDown strokeWidth={1} />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white" align="start">
            {statuses?.map((status) => {
              return (
                <DropdownMenuItem
                  key={status?.value}
                  className="text-xs outline-none hover:bg-gray-100 font-medium hover:cursor-pointer"
                  onSelect={() =>
                    setFilter((prev) => ({ ...prev, status: status?.value }))
                  }
                >
                  {status?.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Ordering Filter */}
        <DropdownMenu>
          <DropdownMenuTrigger
            defaultValue={orderings[0]?.value}
            className="flex items-center justify-between text-left text-charcoal font-medium text-xs  border rounded-md h-max py-1.5 px-2 w-40"
          >
            {orderings?.find((s) => filter?.order === s?.value)?.label}
            <ChevronDown strokeWidth={1} />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white" align="start">
            {orderings?.map((order) => {
              return (
                <DropdownMenuItem
                  key={order?.value}
                  className="text-xs outline-none hover:bg-gray-100 font-medium hover:cursor-pointer"
                  onSelect={() =>
                    setFilter((prev) => ({ ...prev, order: order?.value }))
                  }
                >
                  {order?.label}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-col min-h-0 flex-1">
        {/* Table header */}
        <div className="grid grid-cols-[1.5fr_1.5fr_0.4fr_0.6fr] bg-surface-subtle border px-3 py-2">
          <span className="text-xss font-bold text-light-black uppercase tracking-wider">
            candidate
          </span>
          <span className="text-xss font-bold text-light-black uppercase tracking-wider">
            application summary
          </span>
          <span className="text-xss font-bold text-light-black uppercase tracking-wider">
            match
          </span>
          <span className="text-xss font-bold text-light-black uppercase tracking-wider">
            status
          </span>
        </div>
        {/* Table Body  */}
        {isLoading || !applicantList?.length? (
          <div className="flex flex-1 justify-center items-center">
            {isLoading ? (
              <LoadingAnimation color="black" />
            ) : (
              <div className="flex flex-col items-center text-center gap-1 px-10">
                <span className="text-xs font-semibold text-black">
                  {emptyState?.title}
                </span>
                <span className="text-xss font-medium text-light-black max-w-sm">
                  {emptyState?.desc}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0 overflow-y-scroll no-scrollbar">
            {applicantList?.map((applicant, index) => {
              const { candidateDetails } = applicant;
              return (
                <Link
                  key={applicant._id}
                  to={`/job-applicant-detail/${jobId}?applicationId=${applicant._id}&tab=profile`}
                  aria-label={`Open application for ${[candidateDetails?.firstName, candidateDetails?.lastName].filter(Boolean).join(" ")}`}
                  className="grid grid-cols-[1.5fr_1.5fr_0.4fr_0.6fr] items-center bg-white border-x border-b px-3 py-3 cursor-pointer hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="bg-black rounded-full h-8 w-8 flex items-center justify-center text-xs font-bold text-white">
                      {candidateDetails?.firstName?.charAt(0)}
                      {candidateDetails?.lastName?.charAt(0)}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs font-semibold text-black">
                        {candidateDetails?.firstName}{" "}
                        {candidateDetails?.lastName}
                      </span>
                      <span className="text-xss font-medium text-light-black">
                        {candidateDetails?.jobTitle} ·{" "}
                        {candidateDetails?.location}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <span className="text-xs font-semibold text-black">
                      {applicant?.createdAt ? `Applied ${dayjs(applicant.createdAt).format("DD MMM YYYY · HH:mm")}` : "Application received"}
                    </span>
                    <span className="text-xss font-medium text-light-black">
                      {candidateDetails?.status || "View candidate profile"}
                    </span>
                  </div>

                  <span className="font-semibold text-xs pl-2">
                    {applicant?.matchingPercentage}%
                  </span>

                  <div className="flex items-center gap-7">
                    <div className="text-xss font-semibold text-green-500 bg-green-100 px-2 py-0.5 rounded-full">
                      {applicant?.latestTracker?.status || (applicant?.viewed ? "Reviewed" : "New")}
                    </div>
                    <ArrowRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralApplicationTable;
